// Словарь и мелкие расчёты показа для раздела «Ведомость инженера».
//
// ЧЕГО ЗДЕСЬ НЕТ НАМЕРЕННО: ни одной денежной формулы. Начислено, выплачено, остаток, итоги
// по выборке и расхождения по людям считает ядро (p73g_api.py) — оно же считает их одним
// запросом, согласованными между собой. Вторая реализация той же формулы на фронте однажды
// разойдётся с первой, и разойдётся молча, потому что сверять их некому.
//
// Что здесь есть: названия (вид начисления, статус заявки, уровень), выбор самой отставшей
// сущности синка и ОДНО вычитание — «баланс минус журнал». Оно оставлено потому, что оба
// слагаемых приезжают из ядра одним ответом (counts.total_points и counts.accrued_sum),
// а разность нужна на экране числом. Ядро ту же разность считает по каждому человеку
// (ledger.total_minus_log) и в проверке balance_vs_log — сверить их можно и нужно.

import type {
  БейджСтрока, ПроверкаСтрока, СвежестьP73G, Сходимость, СчётчикиP73G,
} from '../api/p73g'
import { plural } from './format'

/** Та же страница, что PAGE в p73g_api.py. */
export const РАЗМЕР_СТРАНИЦЫ = 50

/** Портал 73: там начисляют, там подают заявку. Раздел CRM только показывает. */
export const ПОРТАЛ_73 = 'https://73.cirspb.com/'
export const ПОРТАЛ_73_БАЛАНС = 'https://73.cirspb.com/#/balance'
export const ПОРТАЛ_73_РЕЙТИНГ = 'https://73.cirspb.com/#/leaderboard'
export const ПОРТАЛ_73_БЕЙДЖИ = 'https://73.cirspb.com/#/badges'

// ───────────────────────────── числа и слова ─────────────────────────────

/** Число с разрядами: 236027 → «236 027». Пусто — прочерк, а не ноль. */
export function число(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—'
  return Number(n).toLocaleString('ru-RU', { maximumFractionDigits: 2 })
}

/** «84 600 баллов», «1 балл», «2 балла». Разряды и склонение вместе. */
export function баллы(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—'
  return `${число(n)} ${plural(Number(n), 'балл', 'балла', 'баллов')}`
}

/** Месяц «ГГГГ-ММ» словами: «2026-07» → «июль 2026». */
const МЕСЯЦЫ = ['январь', 'февраль', 'март', 'апрель', 'май', 'июнь', 'июль', 'август',
  'сентябрь', 'октябрь', 'ноябрь', 'декабрь']

export function месяцСловами(m: string | null | undefined): string {
  const s = String(m ?? '')
  const п = /^(\d{4})-(\d{2})$/.exec(s)
  if (!п) return s || '—'
  const i = Number(п[2]) - 1
  return `${МЕСЯЦЫ[i] ?? п[2]} ${п[1]}`
}

/** Текущий месяц «ГГГГ-ММ» по локальной дате. */
export function текущийМесяц(сегодня: Date = new Date()): string {
  const m = String(сегодня.getMonth() + 1).padStart(2, '0')
  return `${сегодня.getFullYear()}-${m}`
}

// ───────────────────────────── справочные названия ─────────────────────────────
//
// Расшифровки приезжают из ядра (refs.kind_names и соседи) — чтобы одно и то же называлось
// одинаково в разделе, в выгрузке и в ответе на вопрос «что это было». Здесь только правило
// «незнакомого не прячем»: код, которого нет в справочнике, показывается КАК ЕСТЬ. Портал
// заведёт новый вид начисления — на экране появится его код, а не пустая ячейка.

export function поСправочнику(
  код: string | number | null | undefined,
  справочник: Record<string, string> | undefined,
  пусто = '—',
): string {
  if (код === null || код === undefined || код === '') return пусто
  return справочник?.[String(код)] ?? String(код)
}

// ───────────────────────────── свежесть зеркала ─────────────────────────────

/**
 * Самая отставшая сущность раздела, а не средняя по шести. Средняя прячет ровно тот случай,
 * ради которого свежесть и показывают: каталог бейджей перечитывается раз в три часа,
 * и «в среднем свежо» означало бы, что можно верить всему.
 */
export function свежесть(
  список: ВходСвежести[] | undefined,
): { entity: string; age_min: number } | null {
  const с = (список ?? []).filter((x) => x.age_min !== null && x.age_min !== undefined) as {
    entity: string
    age_min: number
  }[]
  if (!с.length) return null
  return с.reduce((a, b) => (b.age_min > a.age_min ? b : a))
}

type ВходСвежести = Pick<СвежестьP73G, 'entity'> & { age_min: number | null }

/** Короткое имя сущности синка для показа человеку. */
export const ИМЯ_СУЩНОСТИ: Record<string, string> = {
  'p73g:points': 'балансы',
  'p73g:bonus_log': 'журнал начислений',
  'p73g:badges': 'каталог бейджей',
  'p73g:user_badges': 'выдачи бейджей',
  'p73g:payouts': 'выплаты',
  'p73g:payout_requests': 'заявки на выплату',
}

// ───────────────────── расхождение баланса с журналом ─────────────────────
//
// ГЛАВНОЕ ЧИСЛО РАЗДЕЛА, И ОНО НЕ ОДНО. «Начислено» в этом зеркале существует в двух видах:
//   • по БАЛАНСАМ  — users.total_points, колонка, которую портал накапливает UPDATE-ами;
//   • по ЖУРНАЛУ   — сумма bonus_log, отдельные INSERT-ы рядом.
// Общей транзакции у портала нет, поэтому эти два числа могут разойтись и разошлись.
// Экран обязан показать ОБА и назвать разницу — «правильного» из них нет, и выбрать одно
// значило бы решить за владельца, какая половина данных врёт.

export interface Расхождение {
  балансы: number
  журнал: number
  разница: number
  сходится: boolean
}

export function расхождение(счёт: СчётчикиP73G | undefined): Расхождение | null {
  if (!счёт) return null
  const балансы = Number(счёт.total_points ?? 0)
  const журнал = Number(счёт.accrued_sum ?? 0)
  return { балансы, журнал, разница: балансы - журнал, сходится: балансы === журнал }
}

/** Проверка сходимости по коду. Нет ответа ядра — нет и проверки, а не «ноль строк». */
export function проверка(
  свод: Сходимость | undefined, код: string,
): ПроверкаСтрока | undefined {
  return свод?.rows.find((р) => р.check === код)
}

/**
 * Кто именно развёл баланс с журналом. Берём ПЕРВЫЙ пример проверки balance_vs_log —
 * ядро сортирует их по модулю расхождения, то есть первым идёт самый крупный.
 *
 * Числа здесь не считаются: delta приходит из ядра готовой.
 */
export interface Виновник {
  d1_id: number
  full_name: string
  total_points: number
  log_points: number
  delta: number
}

export function виновникРасхождения(свод: Сходимость | undefined): Виновник | null {
  const п = проверка(свод, 'balance_vs_log')
  const образец = п?.samples?.[0]
  if (!образец) return null
  return {
    d1_id: Number(образец.d1_id ?? 0),
    full_name: String(образец.full_name ?? `учётка #${образец.d1_id ?? '?'}`),
    total_points: Number(образец.total_points ?? 0),
    log_points: Number(образец.log_points ?? 0),
    delta: Number(образец.delta ?? 0),
  }
}

/**
 * Сходится ли расхождение целиком на одном человеке. Если да — экран может честно сказать
 * «остальные сходятся в копейку»; если нет — такого обещания давать нельзя.
 */
export function однимЧеловеком(
  р: Расхождение | null, в: Виновник | null, п: ПроверкаСтрока | undefined,
): boolean {
  return !!р && !!в && !!п && п.rows === 1 && р.разница === в.delta
}

// ───────────────────────────── мелочи показа ─────────────────────────────

/** Бейдж заведён, но ни разу не выдан. Не поломка — «правило есть, повода не было». */
export const ниРазуНеВыдан = (б: БейджСтрока): boolean => (б.awarded ?? 0) === 0

/**
 * Курс словами. Считается ядром по проведённым выплатам, а не берётся из настроек портала;
 * если выплат ещё нет, курса нет тоже — и придумывать единицу нельзя.
 */
export function курсСловами(счёт: СчётчикиP73G | undefined): string {
  if (!счёт || счёт.rub_per_point === null || счёт.rub_per_point === undefined) {
    return 'курс неизвестен: проведённых выплат в зеркале нет'
  }
  const n = счёт.payouts ?? 0
  return `${число(счёт.rub_per_point)} ₽ за балл — посчитано по ${n} `
    + `${plural(n, 'выплате', 'выплатам', 'выплатам')}, а не взято из настроек портала`
}

/** Состояние заявки на выплату: цвет плашки. Слово берётся из справочника ядра. */
export function классСтатуса(статус: string | null | undefined): string {
  switch (статус) {
    case 'approved': return 'badge won'
    case 'rejected': return 'badge lose'
    case 'pending': return 'badge work'
    default: return 'badge'
  }
}
